"use client";

import Link from "next/link";

/**
 * Presentation for the interactive shell: the `Line` model that
 * history is made of, the prompt, and the per-line renderer the
 * terminal drops inside its `<pre>`.
 */

/** One entry in the terminal history.
 *  - `cmd`: a submitted command, rendered after the prompt for the
 *    `cwd` it ran in.
 *  - `out` / `err`: plain output lines.
 *  - `md`: pre-built React nodes, for output with inline links. */
export type Line =
  | { kind: "cmd"; cwd: string; text: string }
  | { kind: "out" | "err"; text: string }
  | { kind: "md"; nodes: React.ReactNode[] };

const FG = "text-[color:var(--color-fg)]";
const ACCENT = "text-[color:var(--color-accent)]";
const PATH = "text-[color:#5b81bb]";

export const LINK_CLASS =
  "text-[color:#5b81bb] underline decoration-dotted underline-offset-2 hover:text-[color:var(--color-accent)] hover:decoration-solid";

/** `[emily@byemily.org ~/blog]$ `, same format as the prompt the
 *  boot animation shows after login. */
export function Prompt({ cwd }: { cwd: string }) {
  return (
    <>
      <span className={FG}>[</span>
      <span className={ACCENT}>emily@byemily.org</span>
      <span className={FG}> </span>
      <span className={PATH}>{cwd}</span>
      <span className={FG}>]$ </span>
    </>
  );
}

/** Site paths that become links when they appear as bare `/foo`
 *  tokens in output (e.g. "have something to say? /contact." in
 *  about.md). */
const LINKABLE_PATH_RE = new RegExp(
  String.raw`(?<=\s)\/(?:about|contact|blog(?:\/[a-z0-9][a-z0-9._-]*)?|projects(?:\/[a-z0-9][a-z0-9._-]*)?)(?=$|[^a-z0-9._-])`,
  "g"
);

export function linkify(s: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  for (const m of s.matchAll(LINKABLE_PATH_RE)) {
    if (m.index > last) out.push(s.slice(last, m.index));
    out.push(
      <Link key={m.index} href={m[0]} className={LINK_CLASS}>
        {m[0]}
      </Link>
    );
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

export function renderHistoryLine(l: Line, i: number): React.ReactNode {
  switch (l.kind) {
    case "cmd":
      return (
        <span key={i}>
          <Prompt cwd={l.cwd} />
          <span className={FG}>{l.text}</span>
          {"\n"}
        </span>
      );
    case "err":
      return (
        <span key={i} className="text-[color:#e06c75]">
          {l.text}
          {"\n"}
        </span>
      );
    case "md":
      return (
        <span key={i} className="text-[color:var(--color-muted)]">
          {l.nodes}
          {"\n"}
        </span>
      );
    case "out":
      return (
        <span key={i} className="text-[color:var(--color-muted)]">
          {linkify(l.text)}
          {"\n"}
        </span>
      );
  }
}
